{
  "schemaVersion": 1,
  "id": "cube-storage-joints",
  "version": "1.0",
  "name": "Cube storage: egg-crate or housed lattice inside a screwed outer box",
  "family": "cube-storage",
  "familyId": "cube-storage",
  "jointFamily": "mixed",
  "provisional": true,
  "reviewer": null,
  "reviewedAt": null,
  "selfAuthored": true,
  "author": "David Mansaray",
  "authoredAt": "2026-10-09",
  "sources": [
    "research/taxonomy.md §1.2 Cube: egg-crate halvings are safe for solid-wood movement because both members run front to back at the same depth",
    "PLAN.md §5: housed dado one third of the thickness deep; egg-crate halving; rebate for backs; glue-ups from boards up to maxWidth",
    "Grid bookcase joints.json (fasteners, wall fixing, anti-tip, tolerances) carried over"
  ],
  "joint": "An outer box of two sides, a top and a bottom, screwed and glued. Inside, either full-length dividers and shelves cross in egg-crate halvings (each notched half its depth, the dividers from the front, the shelves from the back), or full-width shelves carry short dividers housed one third of the thickness into the shelf above and below.",
  "joints": [
    {
      "at": "bottom-side",
      "kind": "screwed-butt",
      "params": {
        "screwSpec": "4.0 × 50 mm countersunk"
      }
    },
    {
      "at": "top-side",
      "kind": "screwed-butt",
      "params": {
        "screwSpec": "4.0 × 50 mm countersunk"
      }
    },
    {
      "at": "shelf-side",
      "kind": "screwed-butt",
      "params": {
        "screwSpec": "4.0 × 50 mm countersunk"
      },
      "note": "Full-width shelves screwed through the sides."
    },
    {
      "at": "divider-shelf",
      "kind": "egg-crate",
      "note": "Egg-crate: halving joints, each member notched half its depth; housed-dado when the design asks for housed intersections."
    },
    {
      "at": "divider-bottom",
      "kind": "screwed-butt",
      "params": {
        "screwSpec": "4.0 × 50 mm countersunk"
      },
      "note": "Through the bottom and the top into the divider ends."
    },
    {
      "at": "back-carcass",
      "kind": "screwed-butt",
      "params": {
        "screwSpec": "3.5 × 20 mm pan head"
      }
    }
  ],
  "envelope": {
    "W": {
      "min": 3000,
      "max": 20000
    },
    "H": {
      "min": 3000,
      "max": 20000
    },
    "D": {
      "min": 2000,
      "max": 4500
    },
    "n": {
      "min": 1,
      "max": 6
    },
    "minOpeningTicks": 1500
  },
  "limits": {
    "maxSpanMm": 800,
    "minOpeningMm": 150
  },
  "edgeAllowanceTicks": 20,
  "cutAllowanceTicks": {
    "l": 0,
    "w": 0
  },
  "arrisMm": 0.6,
  "intendedUse": "Domestic books and objects; shelves loaded evenly. Not for climbing, not structural.",
  "loadStatement": "Self-authored: no rated load. Books and objects loaded evenly along each shelf; heaviest items on the lowest shelves.",
  "edgeTreatment": "Plywood: exposed ply edges, sanded arris 0.6 mm, lacquered with the faces. Solid timber: planed edges, 1 mm arris, oiled with the faces.",
  "fasteners": [
    {
      "id": "carcass-screw",
      "spec": "4.0 × 50 mm countersunk wood screw",
      "qtyRule": "per-joint",
      "perJoint": 3,
      "use": "Through the sides into each horizontal, 3 per joint at 25 mm from the front and back edges and centred; pilot 2.5 mm, clearance 4.5 mm; PVA glue on all butt joints."
    },
    {
      "id": "back-screw",
      "spec": "3.5 × 20 mm pan-head screw",
      "qtyRule": "back-perimeter-and-shelves",
      "centresMm": 150,
      "use": "Through the back into the sides, top, bottom and every shelf at 150 mm centres, glued."
    },
    {
      "id": "wall-bracket",
      "spec": "Steel angle bracket with fixings suited to the wall",
      "qtyRule": "fixed",
      "qty": 2,
      "use": "Under the top panel into studs, or with masonry plugs or cavity anchors suited to the wall."
    }
  ],
  "backFixing": {
    "method": "Optional back (overlay or rebated), screwed to the sides, top, bottom and every shelf. An open-backed cube relies on the lattice joints and the wall fixing.",
    "spacingMm": 150,
    "rackingNote": "Egg-crate halvings and housings keep the lattice square; an open-backed cube over 1000 mm tall must be fixed to the wall."
  },
  "wallFixing": {
    "required": true,
    "method": "Two steel angle brackets under the top panel, screwed into studs or with fixings suited to the wall (masonry plugs or cavity anchors).",
    "hardware": "2 × steel angle bracket, screws and plugs to suit the wall"
  },
  "antiTip": {
    "required": true,
    "method": "Required above 1000 mm tall: wall fixing as above, plus an anti-tip strap rated for the cabinet. Load the lowest shelves first."
  },
  "warnings": {
    "maxShelfSpanMm": 800,
    "antiTipAboveMm": 1000
  },
  "assemblySteps": [
    "Glue up any solid panels listed as glue-ups first; flatten and cut to finished size.",
    "Cut the egg-crate halvings (or the housings) after glue-up and flattening, all from the same datum edge.",
    "Sand faces to 180 grit and break every arris.",
    "Slot the dividers and shelves together dry; check every cell is square.",
    "Glue and screw the bottom and the top between the sides, then the lattice inside.",
    "Fit the back, if any, squaring the case first.",
    "Finish (two coats, de-nib between).",
    "Install: fix to the wall with two brackets before loading if it is over 1000 mm tall."
  ],
  "markingDrilling": [
    "Mark every shelf datum (underside from the floor) on the inside face of both verticals, measured from the same (bottom) end.",
    "Screw centres at mid-thickness above each datum line, at 25 mm from the front and back edges and centred.",
    "Clearance 4.5 mm and countersink through the sides; pilot 2.5 mm into the panel edges."
  ],
  "installation": [
    "Stand the bookcase in place and check it is level and plumb; pack under the sides if the floor is uneven.",
    "Fix two angle brackets under the top panel into the wall (studs, or plugs or anchors suited to the wall).",
    "Fit the anti-tip strap before loading any shelf.",
    "Load the lowest shelves first."
  ],
  "tolerances": {
    "panelLength": 0.5,
    "panelWidth": 0.5,
    "squareness": "≤ 1 mm over the diagonal",
    "shelfDatum": 1
  },
  "backConstraint": "Each back piece fits one sheet: min(w, h) ≤ 1220 − 2a and max(w, h) ≤ 2440 − 2a, a = 2 mm; a back too wide for one sheet is split over a divider."
}
